import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { dataHookFactory, dataHookWithCredentialsFactory, DefaultFailureAction, DefaultSuccessAction, readonlyHookWithCredentialsFactory } from "../../common/reducers/utilities";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { ensureInteger } from "../../common/utils/generalUtils";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";
import { options } from "sanitize-html";

const key = e => ensureInteger(e.id);

export const equipmentStaffSlice = createSlice({
    name: 'staffEquipment',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            state.data = ensureArray(action.payload).reduce((obj, e) => { obj[key(e)] = e; return obj; }, {});
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(e => state.data[key(e)] = e);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(e => delete state.data[key(e)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => equipmentStaffSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'equipment_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'equipment_one');

const fetchAllEquipment = network => network.get('/api/v2/staffEquipment', equipmentStaffSlice.actions.set, visualizeFailure('get'));

// #endregion

// #region selectors
const selectEquipmentData = state => state.staffEquipment.data;
const selectAllEquipment = createSelector([selectEquipmentData], equipment => Object.values(equipment))
const selectSingleEquipment = id => createSelector([selectEquipmentData], equipment => equipment[id]);
const selectEquipment = ids => createSelector([selectEquipmentData], equipment => ensureArray(ids).reduce((result, id) => { if (equipment[id]) { result.push(equipment[id]) } return result }, []));
// #endregion

const listen = {
    equipmentCreated: (equipment, { dispatch, websocket }) => {
        markFetched(websocket, 'equipment', key(equipment))
        dispatch(equipmentStaffSlice.actions.addOrUpdate(equipment))
    },
    equipmentUpdated: (equipment, { dispatch }) => dispatch(equipmentStaffSlice.actions.addOrUpdate(equipment)),
    equipmentRemoved: (equipment, { dispatch }) => dispatch(equipmentStaffSlice.actions.remove(equipment)),
}

const multiOptions = { condition: credentials => credentials?.hasRole('member') };
const singleOptions = { ...multiOptions, defaultValue: null };
const init = { run: fetchAllEquipment, listen, immortal: true };

export const [useStaffAllEquipment] = dataHookWithCredentialsFactory(selectAllEquipment, init, options);
export const [useStaffEquipment] = dataHookWithCredentialsFactory(selectEquipment, ids => init, options);
export const [useStaffSingleEquipment] = dataHookWithCredentialsFactory(selectSingleEquipment, id => init, singleOptions);

export const [useReadStaffSingleEquipment] = readonlyHookWithCredentialsFactory(selectSingleEquipment, id => init, singleOptions);
export const [useReadStaffEquipment] = readonlyHookWithCredentialsFactory(selectEquipment, ids => init, multiOptions);

const reducer = { name: equipmentStaffSlice.name, reducer: equipmentStaffSlice.reducer }
export default reducer;